/**
 * Centralized Documentation Search Index
 * 
 * Each entry represents a searchable page or section within the documentation.
 * 
 * Structure:
 * - title: Display name shown in search results
 * - path: React Router path for navigation
 * - sectionId: Optional anchor ID for deep linking to specific sections
 * - keywords: Array of searchable terms (lowercase recommended)
 * - category: Grouping for better organization
 */

export const docsIndex = [
  // ==================== INTRODUCTION ====================
  {
    title: "Overview",
    path: "/docs/overview",
    keywords: ["overview", "introduction", "eventflow", "what is", "about"],
    category: "Introduction"
  },
  {
    title: "System Architecture",
    path: "/docs/system-architecture",
    keywords: ["architecture", "system", "design", "structure", "technical"],
    category: "Introduction"
  },
  {
    title: "Tech Stack",
    path: "/docs/tech-stack",
    keywords: ["technology", "stack", "tech", "frameworks", "tools", "react", "node"],
    category: "Introduction"
  },
  {
    title: "Who Is This For",
    path: "/docs/who-is-this-for",
    keywords: ["audience", "users", "who", "target", "banquet", "hall", "venue"],
    category: "Introduction"
  },

  // ==================== GETTING STARTED ====================
  {
    title: "Quick Start",
    path: "/docs/quick-start",
    keywords: ["quick start", "getting started", "begin", "start", "first steps"],
    category: "Getting Started"
  },
  {
    title: "Installation",
    path: "/docs/installation",
    keywords: ["install", "installation", "setup", "download", "deploy"],
    category: "Getting Started"
  },
  {
    title: "Initial Setup",
    path: "/docs/initial-setup",
    keywords: ["initial", "setup", "configuration", "configure", "settings"],
    category: "Getting Started"
  },
  {
    title: "First Event Booking",
    path: "/docs/first-event-booking",
    keywords: ["first", "booking", "event", "tutorial", "guide", "walkthrough"],
    category: "Getting Started"
  },

  // ==================== CORE FEATURES ====================
  {
    title: "Admin Dashboard",
    path: "/docs/admin-dashboard",
    keywords: ["dashboard", "admin", "overview", "analytics", "metrics", "stats"],
    category: "Core Features"
  },

  // Event Booking Management - Main Page
  {
    title: "Event Booking Management",
    path: "/docs/event-booking-management",
    keywords: ["event", "booking", "management", "reservations", "bookings"],
    category: "Core Features"
  },
  // Event Booking Management - Sections
  {
    title: "Event Estimates",
    path: "/docs/event-booking-management",
    sectionId: "event-estimates",
    keywords: ["estimates", "quotation", "quote", "pricing", "proposal"],
    category: "Core Features"
  },
  {
    title: "Add Booking",
    path: "/docs/event-booking-management",
    sectionId: "add-booking",
    keywords: ["add", "create", "new booking", "book event"],
    category: "Core Features"
  },
  {
    title: "Bookings List",
    path: "/docs/event-booking-management",
    sectionId: "bookings",
    keywords: ["bookings", "list", "all bookings", "view bookings"],
    category: "Core Features"
  },
  {
    title: "Receipt Vouchers",
    path: "/docs/event-booking-management",
    sectionId: "receipt-vouchers",
    keywords: ["receipt", "voucher", "payment", "received", "collection"],
    category: "Core Features"
  },
  {
    title: "Complete Booking List",
    path: "/docs/event-booking-management",
    sectionId: "complete-booking-list",
    keywords: ["complete", "completed", "finished", "done"],
    category: "Core Features"
  },
  {
    title: "Shift Booking List",
    path: "/docs/event-booking-management",
    sectionId: "shift-booking-list",
    keywords: ["shift", "shifted", "moved", "rescheduled"],
    category: "Core Features"
  },
  {
    title: "Cancel Booking List",
    path: "/docs/event-booking-management",
    sectionId: "cancel-booking-list",
    keywords: ["cancel", "cancelled", "canceled", "cancellation"],
    category: "Core Features"
  },
  {
    title: "Customer List",
    path: "/docs/event-booking-management",
    sectionId: "customer-list",
    keywords: ["customer", "client", "customers", "clients", "contact"],
    category: "Core Features"
  },
  {
    title: "Refund Voucher",
    path: "/docs/event-booking-management",
    sectionId: "refund-voucher",
    keywords: ["refund", "return", "payback", "reimbursement"],
    category: "Core Features"
  },
  {
    title: "Event Calendar",
    path: "/docs/event-booking-management",
    sectionId: "event-calendar",
    keywords: ["calendar", "schedule", "timeline", "dates", "availability"],
    category: "Core Features"
  },

  // Reports
  {
    title: "Reports",
    path: "/docs/reports",
    keywords: ["reports", "reporting", "analytics", "insights", "data"],
    category: "Core Features"
  },

  // Banquet Setup
  {
    title: "Banquet Setup",
    path: "/docs/banquet-setup",
    keywords: ["banquet", "setup", "hall", "venue", "configuration", "rooms"],
    category: "Core Features"
  },

  // Expenses
  {
    title: "Expenses",
    path: "/docs/expenses",
    keywords: ["expense", "expenses", "costs", "spending", "expenditure"],
    category: "Core Features"
  },

  // Procurement
  {
    title: "Procurement",
    path: "/docs/Procurement",
    keywords: ["procurement", "purchase", "buying", "vendor", "supplier"],
    category: "Core Features"
  },

  // Accounting - Main Page
  {
    title: "Accounting Module",
    path: "/docs/accounting",
    keywords: ["accounting", "finance", "financial", "accounts", "ledger"],
    category: "Core Features"
  },
  // Accounting - Sections
  {
    title: "Opening Balances",
    path: "/docs/accounting",
    sectionId: "opening-balances",
    keywords: ["opening", "balance", "initial", "starting balance"],
    category: "Core Features"
  },
  {
    title: "Chart of Accounts",
    path: "/docs/accounting",
    sectionId: "chart-of-accounts",
    keywords: ["chart", "accounts", "coa", "account structure"],
    category: "Core Features"
  },
  {
    title: "Journal Entries",
    path: "/docs/accounting",
    sectionId: "journal-entries",
    keywords: ["journal", "entry", "entries", "transactions", "posting"],
    category: "Core Features"
  },
  {
    title: "Ledger by Account",
    path: "/docs/accounting",
    sectionId: "ledger-by-account",
    keywords: ["ledger", "account ledger", "transactions", "history"],
    category: "Core Features"
  },
  {
    title: "Trial Balance",
    path: "/docs/accounting",
    sectionId: "trial-balance",
    keywords: ["trial", "balance", "trial balance", "debit", "credit"],
    category: "Core Features"
  },
  {
    title: "Profit & Loss",
    path: "/docs/accounting",
    sectionId: "profit-loss",
    keywords: ["profit", "loss", "p&l", "income", "statement", "pnl"],
    category: "Core Features"
  },
  {
    title: "Balance Sheet",
    path: "/docs/accounting",
    sectionId: "balance-sheet",
    keywords: ["balance sheet", "assets", "liabilities", "equity", "financial position"],
    category: "Core Features"
  },

  // Access Control
  {
    title: "Access Control",
    path: "/docs/access-control",
    keywords: ["access", "control", "permissions", "roles", "users", "security"],
    category: "Core Features"
  },

  // ==================== BUSINESS SOLUTIONS ====================
  {
    title: "Eliminate Manual Registers",
    path: "/docs/no-registers",
    keywords: ["manual", "registers", "paperless", "digital", "automation"],
    category: "Business Solutions"
  },
  {
    title: "Prevent Double Bookings",
    path: "/docs/no-double-booking",
    keywords: ["double", "booking", "conflict", "overlap", "prevention"],
    category: "Business Solutions"
  },
  {
    title: "Real-Time Financial Insights",
    path: "/docs/financial-insights",
    keywords: ["real-time", "financial", "insights", "live", "analytics"],
    category: "Business Solutions"
  },
  {
    title: "Multi-User Access Control",
    path: "/docs/access-control-bi",
    keywords: ["multi-user", "access", "control", "team", "collaboration"],
    category: "Business Solutions"
  },
  {
    title: "Organized Record Keeping",
    path: "/docs/records",
    keywords: ["records", "record keeping", "organization", "documentation"],
    category: "Business Solutions"
  },
  {
    title: "Staff Efficiency",
    path: "/docs/staff-efficiency",
    keywords: ["staff", "efficiency", "productivity", "performance", "team"],
    category: "Business Solutions"
  },

  // ==================== SERVICES ====================
  {
    title: "Customization",
    path: "/docs/customization",
    keywords: ["customization", "customize", "custom", "tailor", "personalize"],
    category: "Services"
  },
  {
    title: "Integration",
    path: "/docs/integration",
    keywords: ["integration", "integrate", "api", "connect", "third-party"],
    category: "Services"
  },
  {
    title: "Data Migration",
    path: "/docs/data-migration",
    keywords: ["migration", "migrate", "import", "transfer", "data"],
    category: "Services"
  },

  // ==================== RESOURCES ====================
  {
    title: "About Us",
    path: "/docs/about",
    keywords: ["about", "company", "team", "who we are"],
    category: "Resources"
  },
  {
    title: "Support",
    path: "/docs/support",
    keywords: ["support", "help", "assistance", "customer service"],
    category: "Resources"
  },
  {
    title: "Contact Us",
    path: "/docs/contact",
    keywords: ["contact", "reach", "email", "phone", "get in touch"],
    category: "Resources"
  },
  {
    title: "FAQs",
    path: "/docs/faqs",
    keywords: ["faq", "faqs", "questions", "answers", "help", "common"],
    category: "Resources"
  }
];

/**
 * Search function - filters the index based on query
 * @param {string} query - Search query
 * @returns {Array} - Filtered results
 */
export function searchDocs(query) {
  if (!query || query.trim() === "") {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();

  return docsIndex.filter(item => {
    // Search in title
    if (item.title.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Search in keywords
    return item.keywords.some(keyword => 
      keyword.toLowerCase().includes(lowerQuery)
    );
  });
}
